import React, { useState, useEffect, useRef, useCallback } from 'react';
import { UserAgent, Registerer, Inviter, SessionState } from 'sip.js';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Phone, PhoneOff, PhoneCall, PhoneIncoming, PhoneMissed,
  Mic, MicOff, Volume2, VolumeX, Pause, Play,
  X, Minimize2, Maximize2, Users, Clock, History,
  Building2, Headphones, PhoneForwarded, Hash
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/App';

// Department icons mapping
const DEPARTMENT_ICONS = {
  'Reception': Building2,
  'Front Desk': Building2,
  'Room Service': Headphones,
  'Housekeeping': Users,
  'Concierge': PhoneForwarded,
  'Restaurant': Headphones,
  'Spa': Headphones,
  'default': Phone
};

const getDepartmentIcon = (department) => {
  return DEPARTMENT_ICONS[department] || DEPARTMENT_ICONS.default;
};

// Format duration
const formatDuration = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

const SIPDialer = ({ 
  hotelId, 
  isPortal = false, 
  guestCredentials = null,
  onClose 
}) => {
  // State
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [activeTab, setActiveTab] = useState('directory');
  const [sipConfig, setSipConfig] = useState(null);
  const [directory, setDirectory] = useState([]);
  const [callLogs, setCallLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Call state
  const [isRegistered, setIsRegistered] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [inCall, setInCall] = useState(false);
  const [callState, setCallState] = useState('idle'); // idle, ringing, connected, ended
  const [currentCall, setCurrentCall] = useState(null);
  const [callDuration, setCallDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isOnHold, setIsOnHold] = useState(false);
  const [dialNumber, setDialNumber] = useState('');
  const [callerInfo, setCallerInfo] = useState(null);
  
  // Refs
  const userAgentRef = useRef(null);
  const registererRef = useRef(null);
  const sessionRef = useRef(null);
  const audioRef = useRef(null);
  const ringToneRef = useRef(null);
  const callTimerRef = useRef(null);

  // Fetch SIP config and directory
  useEffect(() => {
    if (hotelId) {
      fetchSIPData();
    }
  }, [hotelId]);

  // Call duration timer
  useEffect(() => {
    if (callState === 'connected') {
      callTimerRef.current = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
    } else {
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
      }
      if (callState === 'idle') {
        setCallDuration(0);
      }
    }
    return () => {
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
      }
    };
  }, [callState]);

  const fetchSIPData = async () => {
    try {
      setLoading(true);
      
      // Fetch public config and directory
      const [configRes, directoryRes] = await Promise.all([
        api.get(`/sip/public/config/${hotelId}`).catch(() => ({ data: null })),
        api.get(`/sip/public/directory/${hotelId}`).catch(() => ({ data: [] }))
      ]);
      
      if (configRes.data) {
        setSipConfig(configRes.data);
      }
      
      setDirectory(directoryRes.data || []);
      
      // Fetch call logs if authenticated
      if (!isPortal || guestCredentials) {
        try {
          const logsRes = await api.get('/sip/call-logs?limit=20');
          setCallLogs(logsRes.data?.logs || []);
        } catch (e) {
          console.log('Could not fetch call logs');
        }
      }
    } catch (error) {
      console.error('Failed to fetch SIP data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Initialize SIP User Agent
  const initializeSIP = useCallback(async () => {
    if (!sipConfig || !guestCredentials) {
      toast.error('SIP configuration not available');
      return;
    }

    try {
      setIsConnecting(true);
      
      const uri = UserAgent.makeURI(`sip:${guestCredentials.username}@${sipConfig.sip_domain}`);
      if (!uri) {
        throw new Error('Failed to create SIP URI');
      }

      const transportOptions = {
        server: sipConfig.websocket_url
      };

      const userAgentOptions = {
        uri,
        transportOptions,
        authorizationUsername: guestCredentials.username,
        authorizationPassword: guestCredentials.password,
        displayName: guestCredentials.display_name || 'Guest',
        sessionDescriptionHandlerFactoryOptions: {
          peerConnectionConfiguration: {
            iceServers: [
              ...sipConfig.stun_servers.map(s => ({ urls: s })),
              ...sipConfig.turn_servers
            ]
          }
        }
      };

      const userAgent = new UserAgent(userAgentOptions);
      userAgentRef.current = userAgent;

      // Set up event handlers
      userAgent.delegate = {
        onInvite: (invitation) => {
          handleIncomingCall(invitation);
        }
      };

      await userAgent.start();

      // Register
      const registerer = new Registerer(userAgent);
      registererRef.current = registerer;

      registerer.stateChange.addListener((state) => {
        console.log('Registration state:', state);
        setIsRegistered(state === 'Registered');
      });

      await registerer.register();
      
      setIsRegistered(true);
      toast.success('Connected to phone system');
    } catch (error) {
      console.error('SIP initialization failed:', error);
      toast.error('Failed to connect to phone system');
    } finally {
      setIsConnecting(false);
    }
  }, [sipConfig, guestCredentials]);

  // Handle incoming call
  const handleIncomingCall = (invitation) => {
    setCallState('ringing');
    setCallerInfo({
      name: invitation.remoteIdentity.displayName || 'Unknown',
      number: invitation.remoteIdentity.uri.user
    });
    sessionRef.current = invitation;
    
    // Play ringtone
    if (ringToneRef.current) {
      ringToneRef.current.play().catch(() => {});
    }

    invitation.stateChange.addListener((state) => {
      handleSessionStateChange(state);
    });
  };

  // Make outbound call
  const makeCall = async (target) => {
    if (!userAgentRef.current || !isRegistered) {
      toast.error('Not connected to phone system');
      return;
    }

    try {
      const targetUri = UserAgent.makeURI(`sip:${target}@${sipConfig.sip_domain}`);
      if (!targetUri) {
        throw new Error('Invalid number');
      }

      const inviter = new Inviter(userAgentRef.current, targetUri);
      sessionRef.current = inviter;

      inviter.stateChange.addListener((state) => {
        handleSessionStateChange(state);
      });

      setCallState('ringing');
      setCallerInfo({
        name: directory.find(d => d.extension === target)?.name || target,
        number: target
      });

      await inviter.invite({
        sessionDescriptionHandlerOptions: {
          constraints: { audio: true, video: false }
        }
      });

      // Log the call
      try {
        await api.post('/sip/call-logs', {
          caller_id: guestCredentials?.username || 'guest',
          callee_id: target,
          callee_name: directory.find(d => d.extension === target)?.name,
          direction: 'outbound',
          status: 'ringing'
        });
      } catch (e) {
        console.log('Could not log call');
      }
    } catch (error) {
      console.error('Failed to make call:', error);
      toast.error('Failed to make call');
      setCallState('idle');
    }
  };

  // Handle session state changes
  const handleSessionStateChange = (state) => {
    console.log('Session state:', state);
    
    switch (state) {
      case SessionState.Establishing:
        setCallState('ringing');
        break;
      case SessionState.Established:
        setCallState('connected');
        setInCall(true);
        setupAudio();
        // Stop ringtone
        if (ringToneRef.current) {
          ringToneRef.current.pause();
          ringToneRef.current.currentTime = 0;
        }
        break;
      case SessionState.Terminated:
        setCallState('idle');
        setInCall(false);
        setCallerInfo(null);
        setIsMuted(false);
        setIsOnHold(false);
        sessionRef.current = null;
        // Stop ringtone
        if (ringToneRef.current) {
          ringToneRef.current.pause();
          ringToneRef.current.currentTime = 0;
        }
        break;
      default:
        break;
    }
  };

  // Setup audio for the call
  const setupAudio = () => {
    const session = sessionRef.current;
    if (!session || !session.sessionDescriptionHandler) return;

    const sdh = session.sessionDescriptionHandler;
    if (sdh.peerConnection) {
      sdh.peerConnection.ontrack = (event) => {
        if (audioRef.current && event.streams[0]) {
          audioRef.current.srcObject = event.streams[0];
          audioRef.current.play().catch(() => {});
        }
      };
    }
  };

  // Answer incoming call
  const answerCall = async () => {
    if (!sessionRef.current) return;
    
    try {
      await sessionRef.current.accept({
        sessionDescriptionHandlerOptions: {
          constraints: { audio: true, video: false }
        }
      });
    } catch (error) {
      console.error('Failed to answer call:', error);
      toast.error('Failed to answer call');
    }
  };

  // Hang up call
  const hangupCall = () => {
    if (!sessionRef.current) return;
    
    try {
      if (sessionRef.current.state === SessionState.Established) {
        sessionRef.current.bye();
      } else {
        sessionRef.current.cancel();
      }
    } catch (error) {
      console.error('Failed to hang up:', error);
    }
  };

  // Reject incoming call
  const rejectCall = () => {
    if (!sessionRef.current) return;
    
    try {
      sessionRef.current.reject();
    } catch (error) {
      console.error('Failed to reject call:', error);
    }
  };

  // Toggle mute
  const toggleMute = () => {
    if (!sessionRef.current) return;
    
    const sdh = sessionRef.current.sessionDescriptionHandler;
    if (sdh && sdh.peerConnection) {
      const senders = sdh.peerConnection.getSenders();
      senders.forEach(sender => {
        if (sender.track && sender.track.kind === 'audio') {
          sender.track.enabled = isMuted;
        }
      });
      setIsMuted(!isMuted);
    }
  };

  // Toggle hold
  const toggleHold = async () => {
    if (!sessionRef.current) return;
    
    try {
      if (isOnHold) {
        await sessionRef.current.unhold();
      } else {
        await sessionRef.current.hold();
      }
      setIsOnHold(!isOnHold);
    } catch (error) {
      console.error('Failed to toggle hold:', error);
    }
  };

  // Send DTMF
  const sendDTMF = (digit) => {
    if (!sessionRef.current || callState !== 'connected') return;
    
    try {
      sessionRef.current.info({
        requestOptions: {
          body: {
            contentDisposition: 'render',
            contentType: 'application/dtmf-relay',
            content: `Signal=${digit}\r\nDuration=100`
          }
        }
      });
    } catch (error) {
      console.error('Failed to send DTMF:', error);
    }
  };

  // Dial pad click
  const handleDialPadClick = (digit) => {
    if (callState === 'connected') {
      sendDTMF(digit);
    } else {
      setDialNumber(prev => prev + digit);
    }
  };

  // Call from directory
  const callFromDirectory = (entry) => {
    makeCall(entry.extension);
  };

  // Call entered number
  const callEnteredNumber = () => {
    if (dialNumber.trim()) {
      makeCall(dialNumber.trim());
    }
  };

  // Render dial pad
  const renderDialPad = () => {
    const keys = [
      ['1', '2', '3'],
      ['4', '5', '6'],
      ['7', '8', '9'],
      ['*', '0', '#']
    ];

    return (
      <div className="space-y-4">
        <Input
          value={dialNumber}
          onChange={(e) => setDialNumber(e.target.value)}
          placeholder="Enter number"
          className="text-center text-2xl font-mono tracking-wider"
          data-testid="dial-input"
        />
        <div className="grid grid-cols-3 gap-2">
          {keys.flat().map((key) => (
            <Button
              key={key}
              variant="outline"
              className="h-14 text-xl font-semibold hover:bg-gray-100"
              onClick={() => handleDialPadClick(key)}
              data-testid={`dial-key-${key}`}
            >
              {key}
            </Button>
          ))}
        </div>
        <Button
          className="w-full h-12 bg-green-600 hover:bg-green-700 text-white"
          onClick={callEnteredNumber}
          disabled={!dialNumber.trim() || !isRegistered}
          data-testid="call-button"
        >
          <Phone className="h-5 w-5 mr-2" />
          Call
        </Button>
      </div>
    );
  };

  // Render directory
  const renderDirectory = () => (
    <ScrollArea className="h-[300px]">
      <div className="space-y-2 pr-4">
        {directory.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Phone className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>No directory entries</p>
          </div>
        ) : (
          directory.map((entry) => {
            const IconComponent = getDepartmentIcon(entry.department);
            return (
              <div
                key={entry.id}
                className="flex items-center justify-between p-3 rounded-lg border hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => callFromDirectory(entry)}
                data-testid={`directory-entry-${entry.extension}`}
              >
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <IconComponent className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{entry.name}</p>
                    <p className="text-sm text-gray-500">
                      {entry.department && `${entry.department} • `}Ext: {entry.extension}
                    </p>
                  </div>
                </div>
                <Button size="sm" variant="ghost" className="text-green-600 hover:text-green-700 hover:bg-green-50">
                  <Phone className="h-4 w-4" />
                </Button>
              </div>
            );
          })
        )}
      </div>
    </ScrollArea>
  );

  // Render call history
  const renderCallHistory = () => (
    <ScrollArea className="h-[300px]">
      <div className="space-y-2 pr-4">
        {callLogs.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <History className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>No call history</p>
          </div>
        ) : (
          callLogs.map((log) => {
            const isOutbound = log.direction === 'outbound';
            const isMissed = log.status === 'missed';
            const Icon = isMissed ? PhoneMissed : (isOutbound ? PhoneCall : PhoneIncoming);
            const iconColor = isMissed ? 'text-red-500' : (isOutbound ? 'text-blue-500' : 'text-green-500');
            
            return (
              <div
                key={log.id}
                className="flex items-center justify-between p-3 rounded-lg border hover:bg-gray-50"
              >
                <div className="flex items-center gap-3">
                  <Icon className={`h-5 w-5 ${iconColor}`} />
                  <div>
                    <p className="font-medium text-gray-900">
                      {isOutbound ? log.callee_name || log.callee_id : log.caller_name || log.caller_id}
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(log.started_at).toLocaleString()}
                      {log.duration_seconds > 0 && ` • ${formatDuration(log.duration_seconds)}`}
                    </p>
                  </div>
                </div>
                <Badge variant={isMissed ? "destructive" : "secondary"}>
                  {log.status}
                </Badge>
              </div>
            );
          })
        )}
      </div>
    </ScrollArea>
  );

  // Render active call UI
  const renderActiveCall = () => (
    <div className="text-center space-y-4">
      {/* Caller info */}
      <div className="py-6">
        <div className="h-20 w-20 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
          <Phone className="h-10 w-10 text-blue-600" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900">{callerInfo?.name || 'Unknown'}</h3>
        <p className="text-gray-500">{callerInfo?.number}</p>
        {callState === 'connected' && (
          <p className="text-lg font-mono text-green-600 mt-2">{formatDuration(callDuration)}</p>
        )}
        {callState === 'ringing' && (
          <p className="text-orange-500 animate-pulse">
            {sessionRef.current?.constructor?.name === 'Invitation' ? 'Incoming call...' : 'Calling...'}
          </p>
        )}
      </div>

      {/* Call controls */}
      <div className="flex justify-center gap-4">
        {callState === 'ringing' && sessionRef.current?.constructor?.name === 'Invitation' ? (
          <>
            <Button
              size="lg"
              className="h-14 w-14 rounded-full bg-green-600 hover:bg-green-700"
              onClick={answerCall}
              data-testid="answer-call"
            >
              <Phone className="h-6 w-6" />
            </Button>
            <Button
              size="lg"
              variant="destructive"
              className="h-14 w-14 rounded-full"
              onClick={rejectCall}
              data-testid="reject-call"
            >
              <PhoneOff className="h-6 w-6" />
            </Button>
          </>
        ) : callState === 'connected' ? (
          <>
            <Button
              size="lg"
              variant={isMuted ? "destructive" : "outline"}
              className="h-12 w-12 rounded-full"
              onClick={toggleMute}
              data-testid="mute-button"
            >
              {isMuted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
            </Button>
            <Button
              size="lg"
              variant={isOnHold ? "secondary" : "outline"}
              className="h-12 w-12 rounded-full"
              onClick={toggleHold}
              data-testid="hold-button"
            >
              {isOnHold ? <Play className="h-5 w-5" /> : <Pause className="h-5 w-5" />}
            </Button>
            <Button
              size="lg"
              variant="destructive"
              className="h-14 w-14 rounded-full"
              onClick={hangupCall}
              data-testid="hangup-button"
            >
              <PhoneOff className="h-6 w-6" />
            </Button>
          </>
        ) : (
          <Button
            size="lg"
            variant="destructive"
            className="h-14 w-14 rounded-full"
            onClick={hangupCall}
            data-testid="cancel-call"
          >
            <PhoneOff className="h-6 w-6" />
          </Button>
        )}
      </div>

      {/* DTMF Keypad (during call) */}
      {callState === 'connected' && (
        <div className="pt-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setActiveTab('dialpad')}
            className="text-gray-500"
          >
            <Hash className="h-4 w-4 mr-1" />
            Keypad
          </Button>
        </div>
      )}
    </div>
  );

  // If not configured
  if (!sipConfig && !loading) {
    return null;
  }

  return (
    <>
      {/* Audio elements */}
      <audio ref={audioRef} autoPlay />
      <audio ref={ringToneRef} src="/ringtone.mp3" loop />

      {/* Floating button */}
      {!isOpen && (
        <Button
          onClick={() => {
            setIsOpen(true);
            if (!isRegistered && sipConfig && guestCredentials) {
              initializeSIP();
            }
          }}
          className="fixed bottom-6 right-6 h-14 w-14 rounded-full shadow-lg bg-green-600 hover:bg-green-700 z-50"
          data-testid="open-dialer"
        >
          <Phone className="h-6 w-6" />
        </Button>
      )}

      {/* Dialer panel */}
      {isOpen && (
        <Card 
          className={`fixed z-50 shadow-2xl transition-all duration-200 ${
            isMinimized 
              ? 'bottom-6 right-6 w-72' 
              : 'bottom-6 right-6 w-96'
          }`}
          data-testid="dialer-panel"
        >
          <CardHeader className="pb-2 flex flex-row items-center justify-between bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-t-lg">
            <div className="flex items-center gap-2">
              <Phone className="h-5 w-5" />
              <CardTitle className="text-base">Phone</CardTitle>
              {isRegistered && (
                <Badge variant="secondary" className="bg-green-500 text-white text-xs">
                  Connected
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                variant="ghost"
                className="h-8 w-8 p-0 text-white hover:bg-white/20"
                onClick={() => setIsMinimized(!isMinimized)}
              >
                {isMinimized ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-8 w-8 p-0 text-white hover:bg-white/20"
                onClick={() => {
                  setIsOpen(false);
                  onClose?.();
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>

          {!isMinimized && (
            <CardContent className="pt-4">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="h-8 w-8 rounded-full border-4 border-blue-500 border-t-transparent animate-spin" />
                </div>
              ) : callState !== 'idle' ? (
                renderActiveCall()
              ) : (
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="directory" data-testid="tab-directory">
                      <Users className="h-4 w-4 mr-1" />
                      Directory
                    </TabsTrigger>
                    <TabsTrigger value="dialpad" data-testid="tab-dialpad">
                      <Hash className="h-4 w-4 mr-1" />
                      Dial
                    </TabsTrigger>
                    <TabsTrigger value="history" data-testid="tab-history">
                      <History className="h-4 w-4 mr-1" />
                      History
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value="directory" className="mt-4">
                    {renderDirectory()}
                  </TabsContent>
                  <TabsContent value="dialpad" className="mt-4">
                    {renderDialPad()}
                  </TabsContent>
                  <TabsContent value="history" className="mt-4">
                    {renderCallHistory()}
                  </TabsContent>
                </Tabs>
              )}

              {/* Connection status */}
              {!isRegistered && !loading && sipConfig && (
                <div className="mt-4 pt-4 border-t">
                  <Button
                    className="w-full"
                    onClick={initializeSIP}
                    disabled={isConnecting}
                    data-testid="connect-sip"
                  >
                    {isConnecting ? 'Connecting...' : 'Connect to Phone System'}
                  </Button>
                </div>
              )}
            </CardContent>
          )}

          {/* Minimized view during call */}
          {isMinimized && callState !== 'idle' && (
            <CardContent className="py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`h-3 w-3 rounded-full ${callState === 'connected' ? 'bg-green-500' : 'bg-orange-500 animate-pulse'}`} />
                  <span className="text-sm font-medium">{callerInfo?.name}</span>
                </div>
                {callState === 'connected' && (
                  <span className="text-sm font-mono text-green-600">{formatDuration(callDuration)}</span>
                )}
                <Button
                  size="sm"
                  variant="destructive"
                  className="h-8 w-8 p-0 rounded-full"
                  onClick={hangupCall}
                >
                  <PhoneOff className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          )}
        </Card>
      )}
    </>
  );
};

export default SIPDialer;
